<script setup lang="ts">
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui'

/**
 * Non-modal floating panel: column choosers, filter builders, date pickers,
 * the account-lookup preview. Unlike `EDialog` it does not trap focus or lock
 * scroll, so the page behind stays fully usable.
 */

withDefaults(
  defineProps<{
    side?: 'top' | 'right' | 'bottom' | 'left'
    align?: 'start' | 'center' | 'end'
    /** Accessible name for the panel; required when it has no visible heading. */
    label?: string
    showArrow?: boolean
    /** Width in px. Omit to size to content. */
    width?: number
  }>(),
  { side: 'bottom', align: 'start', label: '' },
)

const open = defineModel<boolean>('open', { default: false })
</script>

<template>
  <PopoverRoot v-model:open="open">
    <PopoverTrigger as-child>
      <slot name="trigger" />
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        class="e-popover e-popover__panel"
        :side="side"
        :align="align"
        :side-offset="6"
        :style="width === undefined ? undefined : { width: `${width}px` }"
        :aria-label="label === '' ? undefined : label"
      >
        <slot :close="() => (open = false)" />
        <PopoverArrow v-if="showArrow" class="e-popover__arrow" :width="10" :height="5" />
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style>
.e-popover__panel {
  z-index: var(--erpx-z-dropdown);
  padding: var(--erpx-space-3);
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-sm);
  /* Scroll, not spill, past the shared surface's max-height. */
  overflow: auto;
}

.e-popover__panel:focus-visible {
  outline: none;
}

.e-popover__arrow {
  fill: var(--erpx-color-surface-overlay);
  stroke: var(--erpx-color-border-subtle);
}
</style>
